import { Megaphone, Plus } from 'lucide-react';
import { palette, admin, fontFamily, transition } from '../../../styles/tokens';
import { EmptyState } from '../../../components/common/EmptyState';
import type { Announcement } from '../types';

interface Props {
  announcements: Announcement[];
  loading: boolean;
  onCompose: () => void;
}

const SCOPE_META: Record<string, { label: string; bg: string; fg: string }> = {
  all: { label: 'Everyone', bg: palette.blueWash, fg: palette.blue },
  employees: { label: 'Employees', bg: palette.greenWash, fg: palette.green },
  contractors: { label: 'Contractors', bg: palette.amberWash, fg: palette.amber },
  specific: { label: 'Specific people', bg: palette.purpleWash, fg: palette.purple },
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
}

export function AnnouncementsView({ announcements, loading, onCompose }: Props) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: palette.bg, height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px', borderBottom: `1px solid ${admin.border}`,
        background: palette.white, flexShrink: 0,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18, flexShrink: 0,
          background: palette.amberWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Megaphone size={18} color={palette.amber} strokeWidth={1.9} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, fontFamily }}>Announcements</div>
          <div style={{ fontSize: 11, color: palette.g500 }}>Broadcast to your team</div>
        </div>
        <button
          onClick={onCompose}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            height: 32, borderRadius: 16, border: 'none',
            background: palette.accent, color: palette.white,
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            padding: '0 14px', fontFamily,
            transition: `background ${transition.fast}`,
          }}
        >
          <Plus size={15} strokeWidth={2.4} />
          New announcement
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: 20 }}>
          {loading && announcements.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: palette.g400 }}>Loading...</div>
          ) : announcements.length === 0 ? (
            <EmptyState
              icon={<Megaphone size={20} />}
              title="No announcements yet"
              subtitle="Broadcast a message to your whole team, your employees, or your contractors."
              actionLabel="New announcement"
              onAction={onCompose}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {announcements.map(a => {
                const meta = SCOPE_META[a.announcement_scope ?? 'all'] ?? SCOPE_META.all;
                return (
                  <div key={a.id} style={{
                    background: palette.white, borderRadius: admin.cardRadius,
                    border: `1px solid ${admin.border}`, padding: 16,
                    fontFamily,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: palette.g800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.senderName}
                        </span>
                        <span style={{
                          fontSize: 10.5, fontWeight: 600, color: meta.fg, background: meta.bg,
                          borderRadius: 6, padding: '2px 8px', flexShrink: 0,
                        }}>
                          {meta.label}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, color: palette.g400, flexShrink: 0 }}>{formatWhen(a.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.5, color: palette.g700, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {a.content}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
